import { supabase } from './supabaseClient';

//Represents a category or type of tag (e.g., "Status", "Priority", "Category")
export type TagDefinition = {
  id: string;
  organization_id: string;
  key: string;
  description?: string;
  created_at: string;
  updated_at: string;
};

//Represents specific values within a tag definition (e.g., "In Progress", "High", "Electronics")
export type TagValue = {
  id: string;
  tag_definition_id: string;
  value: string;
  metadata: Record<string, string | number | boolean | null>;
  created_at: string;
  updated_at: string;
  organization_id: string;
};

export type ProductTag = {
  id: string;
  tag_value_id: string;
  tag_values: {
    id: string;
    value: string;
    metadata: Record<string, string | number | boolean | null>;
    tag_definitions: {
      id: string;
      key: string;
      description: string;
    }[];
  };
};

export type Tag = {
  id: string;
  organization_id: string;
  name: string;
  color: string;
};

export type ProductTagWithRelations = {
  id: string;
  product_id: string;
  tag_value_id: string;
  tag_values: {
    id: string;
    value: string;
    metadata: Record<string, string | number | boolean | null>;
    tag_definitions: {
      id: string;
      key: string;
      description: string;
    }[];
  }[];
};

export class TagsClient {
  static async getTagsByOrganizationId(organizationId: string) {
    const { data, error } = await supabase
      .from('tags')
      .select('*')
      .eq('organization_id', organizationId);

    if (error) throw error;
    return data;
  }

  static async createTag(tag: Omit<Tag, 'id' | 'created_at' | 'updated_at'>) {
    const { data, error } = await supabase.from('tags').insert(tag).select();

    if (error) throw error;
    return data[0];
  }

  static async updateTag(tag: Omit<Tag, 'created_at' | 'updated_at'>) {
    const { data, error } = await supabase
      .from('tags')
      .update(tag)
      .eq('id', tag.id)
      .select();

    if (error) throw error;
    return data[0];
  }

  static async deleteTag(tagId: string) {
    const { error } = await supabase.from('tags').delete().eq('id', tagId);

    if (error) throw error;
  }

  // Tag Definition methods
  static async getTagDefinitionsByOrganizationId(organizationId: string) {
    const { data, error } = await supabase
      .from('tag_definitions')
      .select('*')
      .eq('organization_id', organizationId)
      .order('key');

    if (error) throw error;
    return data;
  }

  static async createTagDefinition(
    tagDefinition: Omit<TagDefinition, 'id' | 'created_at' | 'updated_at'>
  ): Promise<TagDefinition> {
    const { data, error } = await supabase
      .from('tag_definitions')
      .insert(tagDefinition)
      .select();

    if (error) throw error;
    return data[0];
  }

  static async updateTagDefinition(
    tagDefinition: Omit<TagDefinition, 'created_at' | 'updated_at'>
  ) {
    const { data, error } = await supabase
      .from('tag_definitions')
      .update(tagDefinition)
      .eq('id', tagDefinition.id)
      .select();

    if (error) throw error;
    return data[0];
  }

  static async deleteTagDefinition(tagDefinitionId: string) {
    const { error } = await supabase
      .from('tag_definitions')
      .delete()
      .eq('id', tagDefinitionId);

    if (error) throw error;
  }

  static async getTagValuesByOrganizationId(organizationId: string) {
    const { data, error } = await supabase
      .from('tag_values')
      .select('*')
      .eq('organization_id', organizationId)
      .order('value');

    if (error) throw error;
    return data;
  }

  // Tag Value methods
  static async getTagValuesByDefinitionId(tagDefinitionId: string) {
    const { data, error } = await supabase
      .from('tag_values')
      .select('*')
      .eq('tag_definition_id', tagDefinitionId)
      .order('value');

    if (error) throw error;
    return data;
  }

  static async createTagValue(
    tagValue: Omit<TagValue, 'id' | 'created_at' | 'updated_at'>
  ) {
    const { data, error } = await supabase
      .from('tag_values')
      .insert(tagValue)
      .select();

    if (error) throw error;
    return data[0];
  }

  static async updateTagValue(
    tagValue: Omit<TagValue, 'created_at' | 'updated_at'>
  ) {
    const { data, error } = await supabase
      .from('tag_values')
      .update(tagValue)
      .eq('id', tagValue.id)
      .select();

    if (error) throw error;
    return data[0];
  }

  static async deleteTagValue(tagValueId: string) {
    const { error } = await supabase
      .from('tag_values')
      .delete()
      .eq('id', tagValueId);

    if (error) throw error;
  }

  // Product Tag methods
  static async getProductTags(productId: string): Promise<ProductTag[]> {
    const { data, error } = await supabase
      .from('product_tags')
      .select(
        `
        id,
        tag_value_id,
        tag_values (
          id,
          value,
          metadata,
          tag_definitions (
            id,
            key,
            description
          )
        )
      `
      )
      .eq('product_id', productId);

    if (error) throw error;
    return data as unknown as ProductTag[];
  }

  static async addProductTag(productId: string, tagValueId: string) {
    const { error } = await supabase.from('product_tags').insert({
      product_id: productId,
      tag_value_id: tagValueId,
    });

    if (error) throw error;
  }

  static async removeProductTag(productId: string, tagValueId: string) {
    const { error } = await supabase
      .from('product_tags')
      .delete()
      .match({ product_id: productId, tag_value_id: tagValueId });

    if (error) throw error;
  }

  // Helper methods for common operations
  static async getProductsByTagValue(
    tagValueId: string,
    organizationId: string
  ) {
    const { data, error } = await supabase
      .from('product_tags')
      .select(
        `
        product_id,
        products (
          id,
          name,
          sku,
          description,
          price,
          organization_id
        )
      `
      )
      .eq('tag_value_id', tagValueId)
      .eq('products.organization_id', organizationId);

    if (error) throw error;
    return data.map((item) => item.products);
  }

  static async getProductsByTagDefinition(
    tagDefinitionId: string,
    organizationId: string
  ) {
    const { data, error } = await supabase
      .from('product_tags')
      .select(
        `
        product_id,
        products (
          id,
          name,
          sku,
          description,
          price,
          organization_id
        )
      `
      )
      .eq('tag_values.tag_definition_id', tagDefinitionId)
      .eq('products.organization_id', organizationId);

    if (error) throw error;
    return data.map((item) => item.products);
  }

  static async getAllProductTags(
    organizationId: string
  ): Promise<ProductTagWithRelations[]> {
    const { data, error } = await supabase
      .from('product_tags')
      .select(
        `
        id,
        product_id,
        tag_value_id,
        products!inner (
          id,
          organization_id
        ),
        tag_values (
          id,
          value,
          metadata,
          tag_definitions (
            id,
            key,
            description
          )
        )
      `
      )
      .eq('products.organization_id', organizationId);

    if (error) throw error;
    return data as ProductTagWithRelations[];
  }
}
